import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../utils/api';
import { toast } from 'sonner';

/**
 * PaymentIntentRefundDialog — Admin refunds one or more line items from a
 * PAID (or partially_refunded) payment intent.
 *
 * Flow:
 *   1. `intents/pick`  → parent picks which bookings to refund (all
 *      already-refunded ones are pre-disabled).
 *   2. `draft`         → we POST refund with `confirm:false` and show
 *      the server-computed amount (includes prorated sibling discount).
 *   3. `done`          → we POST refund with `confirm:true`. On success we
 *      close the dialog and let the parent list refresh.
 */
export function PaymentIntentRefundDialog({ open, onClose, intentId, onRefunded }) {
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [intent, setIntent] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [reason, setReason] = useState('');
  const [phase, setPhase] = useState('pick'); // pick | draft | done
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!open || !intentId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');
    setDraft(null);
    setReason('');
    setPhase('pick');
    setSelected(new Set());
    (async () => {
      try {
        const data = await api.getPaymentIntent(intentId);
        if (cancelled) return;
        setIntent(data);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Failed to load payment intent');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, intentId]);

  const currency = intent?.currency || 'GBP';
  const symbol = currency === 'GBP' ? '£' : `${currency} `;

  const { bookingItems, alreadyRefundedIds } = useMemo(() => {
    if (!intent) return { bookingItems: [], alreadyRefundedIds: new Set() };
    const items = (intent.line_items || []).filter((li) => li.kind === 'booking' || (!li.kind && li.booking_id));
    const done = new Set();
    (intent.refunds || []).forEach((r) => (r.booking_ids || []).forEach((b) => done.add(b)));
    return { bookingItems: items, alreadyRefundedIds: done };
  }, [intent]);

  const toggle = (bookingId) => {
    if (alreadyRefundedIds.has(bookingId)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(bookingId)) next.delete(bookingId); else next.add(bookingId);
      return next;
    });
  };

  const selectAll = () => {
    const all = bookingItems
      .filter((li) => !alreadyRefundedIds.has(li.booking_id))
      .map((li) => li.booking_id);
    setSelected(new Set(all));
  };

  const clearAll = () => setSelected(new Set());

  const requestDraft = async () => {
    if (selected.size === 0) {
      toast.error('Select at least one booking to refund.');
      return;
    }
    setError('');
    setBusy(true);
    try {
      const res = await api.refundPaymentIntent(intent.intent_id, {
        booking_ids: [...selected],
        reason: reason.trim() || undefined,
        confirm: false,
      });
      setDraft(res);
      setPhase('draft');
    } catch (err) {
      const msg = err?.message || 'Failed to prepare refund';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const confirmRefund = async () => {
    setError('');
    setBusy(true);
    try {
      const res = await api.refundPaymentIntent(intent.intent_id, {
        booking_ids: [...selected],
        reason: reason.trim() || undefined,
        confirm: true,
      });
      setPhase('done');
      toast.success(`Refunded ${symbol}${Number(res.refund_amount || 0).toFixed(2)}`);
      onRefunded?.(res);
    } catch (err) {
      const msg = err?.message || 'Refund failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (busy) return;
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="intent-refund-dialog"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 240,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div style={{
        width: 'min(620px,100%)', maxHeight: '92vh', background: 'hsl(var(--background))',
        borderRadius: 12, boxShadow: '0 24px 48px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem 1.25rem', borderBottom: '1px solid hsl(var(--border))',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
        }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem' }} data-testid="intent-refund-title">
              {phase === 'done' ? 'Refund processed' :
               phase === 'draft' ? 'Confirm refund' : 'Refund bookings'}
            </h2>
            {intent && (
              <div style={{ fontSize: '0.8rem', color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
                Intent {intent.intent_id?.slice(0, 8)}… · Paid {symbol}{Number(intent.total_amount || 0).toFixed(2)}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="btn btn-ghost"
            aria-label="Close"
            data-testid="intent-refund-close"
            disabled={busy}
            style={{ padding: '0.35rem 0.6rem', fontSize: '1.05rem' }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: '1rem 1.25rem', overflowY: 'auto', flex: '1 1 auto' }}>
          {loading && (
            <div style={{ padding: '1.5rem 0', color: 'hsl(var(--muted-foreground))' }}
                 data-testid="intent-refund-loading">
              Loading payment intent…
            </div>
          )}

          {!loading && !intent && error && (
            <div className="card" style={{
              borderLeft: '4px solid hsl(0 65% 50%)', color: 'hsl(0 60% 35%)',
              padding: '0.75rem 1rem',
            }} data-testid="intent-refund-load-error">
              {error}
            </div>
          )}

          {!loading && intent && phase === 'pick' && (
            <>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem' }}>
                Select the booking(s) to refund. Amounts include any prorated sibling discount.
              </p>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <button type="button" className="btn btn-ghost btn-sm"
                        onClick={selectAll} data-testid="intent-refund-select-all"
                        disabled={busy}>
                  Select all refundable
                </button>
                <button type="button" className="btn btn-ghost btn-sm"
                        onClick={clearAll} data-testid="intent-refund-clear"
                        disabled={busy}>
                  Clear
                </button>
              </div>

              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}
                  data-testid="intent-refund-items">
                {bookingItems.length === 0 && (
                  <li style={{ color: 'hsl(var(--muted-foreground))', padding: '0.75rem 0' }}>
                    No booking line items on this intent.
                  </li>
                )}
                {bookingItems.map((li) => {
                  const already = alreadyRefundedIds.has(li.booking_id);
                  const checked = selected.has(li.booking_id);
                  return (
                    <li key={li.booking_id}
                        data-testid={`intent-refund-item-${li.booking_id}`}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'auto minmax(0,1fr) auto',
                          columnGap: '0.75rem', padding: '0.6rem 0',
                          borderTop: '1px solid hsl(var(--border))',
                          alignItems: 'center',
                          opacity: already ? 0.55 : 1,
                        }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={already || busy}
                        onChange={() => toggle(li.booking_id)}
                        data-testid={`intent-refund-check-${li.booking_id}`}
                        aria-label={`Refund ${li.child_name || 'booking'}`}
                      />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                          {li.child_name || 'Child'}
                          {li.session_label ? ` · ${li.session_label}` : ''}
                          {already && (
                            <span style={{
                              marginLeft: 8, fontSize: '0.7rem',
                              padding: '1px 6px', borderRadius: 4,
                              background: 'hsl(var(--muted))', color: 'hsl(var(--muted-foreground))',
                            }}>Already refunded</span>
                          )}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
                          {li.session_date || '—'}
                        </div>
                      </div>
                      <div style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {symbol}{Number(li.subtotal || 0).toFixed(2)}
                      </div>
                    </li>
                  );
                })}
              </ul>

              <label style={{
                display: 'block', marginTop: '1rem',
                fontSize: '0.85rem', fontWeight: 600,
              }} htmlFor="intent-refund-reason">
                Reason <span style={{ color: 'hsl(var(--muted-foreground))', fontWeight: 400 }}>(optional, shown on parent's receipt)</span>
              </label>
              <textarea
                id="intent-refund-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Nursery closed on this date"
                data-testid="intent-refund-reason"
                rows={2}
                maxLength={255}
                disabled={busy}
                style={{
                  width: '100%', marginTop: 6, padding: '0.5rem 0.65rem',
                  fontFamily: 'inherit', fontSize: '0.9rem',
                  border: '1px solid hsl(var(--border))', borderRadius: 8,
                  resize: 'vertical',
                }}
              />

              {error && (
                <div className="card" style={{
                  marginTop: '0.75rem',
                  borderLeft: '4px solid hsl(0 65% 50%)', color: 'hsl(0 60% 35%)',
                  fontSize: '0.85rem', padding: '0.5rem 0.75rem',
                }} data-testid="intent-refund-error">{error}</div>
              )}
            </>
          )}

          {!loading && intent && phase === 'draft' && draft && (
            <>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem' }}>
                Please review before we send the refund.
              </p>
              <div className="card" style={{ padding: '0.85rem', marginBottom: '0.75rem' }}>
                <Row label="Bookings to refund" value={draft.booking_ids?.length ?? 0} />
                <Row label="Line total" value={`${symbol}${Number(draft.line_total || 0).toFixed(2)}`} />
                {Number(draft.prorated_discount || 0) !== 0 && (
                  <Row
                    label="Prorated sibling discount"
                    value={`${symbol}${Number(draft.prorated_discount || 0).toFixed(2)}`}
                  />
                )}
                <Row
                  label="Refund amount"
                  value={`${symbol}${Number(draft.refund_amount || 0).toFixed(2)}`}
                  highlight
                />
                <Row label="Method" value={humanMethod(draft.method)} />
                {reason && <Row label="Reason" value={reason} />}
              </div>
              <div style={{
                padding: '0.55rem 0.75rem', borderRadius: 8,
                background: 'hsl(var(--muted) / 0.5)',
                fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.4,
              }}>
                {draft.method === 'mollie_intent' || draft.method === 'mollie'
                  ? 'The refund will be issued via Mollie to the parent\'s original card. It typically appears within 5–10 working days.'
                  : 'This is a manual refund — no card gateway will be called. Please issue the refund via your bank / cash separately and keep the audit trail below.'}
              </div>

              {error && (
                <div className="card" style={{
                  marginTop: '0.75rem',
                  borderLeft: '4px solid hsl(0 65% 50%)', color: 'hsl(0 60% 35%)',
                  fontSize: '0.85rem', padding: '0.5rem 0.75rem',
                }} data-testid="intent-refund-confirm-error">{error}</div>
              )}
            </>
          )}

          {phase === 'done' && (
            <div style={{ padding: '0.5rem 0' }} data-testid="intent-refund-success">
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
              <h3 style={{ margin: '0 0 0.4rem', fontSize: '1rem' }}>
                Refund of {symbol}{Number(draft?.refund_amount || 0).toFixed(2)} processed
              </h3>
              <p style={{ margin: 0, fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))' }}>
                The parent has been notified and the affected bookings are marked as refunded.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '0.85rem 1.25rem 1rem',
          borderTop: '1px solid hsl(var(--border))',
          background: 'hsl(var(--muted) / 0.35)',
          display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end',
        }}>
          {phase === 'pick' && (
            <>
              <button type="button" onClick={handleClose}
                      className="btn btn-ghost" data-testid="intent-refund-cancel"
                      disabled={busy}>Cancel</button>
              <button type="button" onClick={requestDraft}
                      className="btn btn-primary" data-testid="intent-refund-preview"
                      disabled={busy || selected.size === 0}>
                {busy ? 'Preparing…' : `Preview refund (${selected.size})`}
              </button>
            </>
          )}
          {phase === 'draft' && (
            <>
              <button type="button" onClick={() => setPhase('pick')}
                      className="btn btn-ghost" data-testid="intent-refund-back"
                      disabled={busy}>← Back</button>
              <button type="button" onClick={confirmRefund}
                      className="btn btn-primary" data-testid="intent-refund-confirm"
                      disabled={busy}
                      style={{ background: 'hsl(0 65% 45%)', color: '#fff' }}>
                {busy ? 'Refunding…' : `Refund ${symbol}${Number(draft?.refund_amount || 0).toFixed(2)}`}
              </button>
            </>
          )}
          {phase === 'done' && (
            <button type="button" onClick={handleClose}
                    className="btn btn-primary" data-testid="intent-refund-done">
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function humanMethod(m) {
  switch (m) {
    case 'mollie':
    case 'mollie_intent':
      return 'Card via Mollie';
    case 'bank_transfer':
      return 'Bank transfer (manual)';
    case 'manual':
      return 'Manual';
    default:
      return m || 'Manual';
  }
}

function Row({ label, value, highlight }) {
  return (
    <div style={{
      display: 'flex', padding: '0.3rem 0', gap: '0.75rem',
      fontSize: '0.88rem',
      borderTop: '1px solid hsl(var(--border) / 0.4)',
    }}>
      <div style={{ width: 200, color: 'hsl(var(--muted-foreground))', flex: '0 0 auto' }}>{label}</div>
      <div style={{
        flex: 1, fontWeight: highlight ? 700 : 500,
        fontVariantNumeric: 'tabular-nums',
        color: highlight ? 'hsl(0 65% 40%)' : 'inherit',
      }}>{value ?? '—'}</div>
    </div>
  );
}

export default PaymentIntentRefundDialog;
